import { ModelTransformer } from '../common/common.model';
import { PeriodType } from '../widget/date-range.widget.model';

import { SuperGroupQueue } from './models/super-group-queue.model';
import { OptionalColumnType } from './optional-column.insight.model';
import { CacheDateItem } from './performance-data/cache-date.pd.model';
import {
    createMetricDataItem,
    FilterContext,
} from './performance-data/data-filter-options.pd.model';
import {
    CriteriaIdType,
    MetricData,
    MetricDataItem,
    PerformanceDataViewType,
} from './performance-data/metric.pd.model';

interface DateRange {
    start: Date;
    end: Date;
}

interface Attribute {
    name: string;
    value: string;
}

export interface Layout {
    breakpoint: number;
    width: number;
    itemsPerRow: number;
}

interface BaseViewMetric<TMetric extends BaseViewMetric<TMetric>> {
    metricId: number;
    formatString: string;
    displayOrder: number;
    supportedColumns: OptionalColumnType[];
    components: TMetric[];
}

interface BaseViewPillar<
    TPillar extends BaseViewPillar<TPillar, TMetric>,
    TMetric extends BaseViewMetric<TMetric>,
> {
    name: string;
    shortName: string;
    initials: string;
    displayOrder: number;
    displayHeader: boolean;
    pillars: TPillar[];
    metrics: TMetric[];
    layouts: Layout[];
}

export const PillarsInGrid = {
    SELECTED: 'selected',
    ALL: 'all',
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PillarsInGrid = 'selected' | 'all';

interface BaseView<
    TPillar extends BaseViewPillar<TPillar, TMetric>,
    TMetric extends BaseViewMetric<TMetric>,
> {
    viewIdentifier: string;
    showPeerGroupVisual: boolean;
    showThresholdColours: boolean;
    showTrendArrows: boolean;
    showSuperGroupQueueAssociation: boolean;
    pillarsInGrid: PillarsInGrid; // TODO revise
    pillarHeadersExpandedInGrid: boolean; // TODO revise
    defaultPathIdentifier: string;
    pillars: TPillar[];
    attributes: Attribute[];
    layouts: Layout[];
}

interface BasePathLevel<TLevel extends BasePathLevel<TLevel>> {
    identifier: string;
    name: string;
    shortName: string;
    initials: string;
    performanceDataViewType: PerformanceDataViewType;
    displayNameCriteriaIdType: CriteriaIdType;
    outputCriteriaIdType: CriteriaIdType; // TODO remove after old pi is removed
    outputCriteriaIdTypes: CriteriaIdType[];
    inputCriteriaIdTypes: CriteriaIdType[];
    inputCriteriaIdTypeArgs: string[];
    displayOrder: number;
    allowNewTab: boolean;
    newTabViewType: string;
    newTabPathType: string;
    previousLevelsRecurse: number;
    levels: TLevel[];
}

interface BasePath<TLevel extends BasePathLevel<TLevel>> {
    pathIdentifier: string;
    name: string;
    shortName: string;
    initials: string;
    displayOrder: number;
    levels: TLevel[];
}

interface BaseDefinitionShallow {
    definitionIdentifier: string;
    categoryIdentifier: string;
    subCategoryIdentifier: string;
    name: string;
    shortName: string;
    initials: string;
    initialCriteriaIdType: CriteriaIdType;
    initialCriteriaIdTypeArg: string;
    defaultPeriod: PeriodType;
    periods: PeriodType[];
    dashboardDateRanges: DateRange[];
}

interface BaseDefinition<
    TView extends BaseView<TPillar, TMetric>,
    TPath extends BasePath<TLevel>,
    TPillar extends BaseViewPillar<TPillar, TMetric>,
    TMetric extends BaseViewMetric<TMetric>,
    TLevel extends BasePathLevel<TLevel>,
> extends BaseDefinitionShallow {
    views: TView[];
    paths: TPath[];
}

/// /////////////////

export interface ViewMetric extends BaseViewMetric<ViewMetric> {}

export interface ViewPillar extends BaseViewPillar<ViewPillar, ViewMetric> {
    clientLanguageTranslationId: number;
}

export interface View extends BaseView<ViewPillar, ViewMetric> {
    profileAssociations: ProfileAssociation[];
    groupAssociations: GroupAssociation[];
    userAssociations: UserAssociation[];
}

export interface PathLevel extends BasePathLevel<PathLevel> {
    clientLanguageTranslationId: number;
}

interface ProfileAssociation {
    roleId: number;
    workGroupType: number;
    workGroupId: number;
    locationType: number;
    locationId: number;
    disallowSetDefault: boolean;
    disallowSetFavourite: boolean;
    defaultPathIdentifier: string;
    isDefault: boolean;
}

interface GroupAssociation {
    securityGroupId: number;
    disallowSetDefault: boolean;
    disallowSetFavourite: boolean;
    defaultPathIdentifier: string;
    isDefault: boolean;
}

interface UserAssociation {
    userId: number;
    disallowSetDefault: boolean;
    disallowSetFavourite: boolean;
    defaultPathIdentifier: string;
    isDefault: boolean;
}

export interface Path extends BasePath<PathLevel> {
    clientLanguageTranslationId: number;
    profileAssociations: ProfileAssociation[];
    groupAssociations: GroupAssociation[];
    userAssociations: UserAssociation[];
}

export interface Definition
    extends BaseDefinition<View, Path, ViewPillar, ViewMetric, PathLevel> {
    clientLanguageTranslationId: number;
    quickSwitchType: string;
    enabled: boolean;
}

interface BaseCategoryDefinition {
    categoryIdentifier: string;
    name: string;
    shortName: string;
    initials: string;
}

export interface DefinitionSummary {
    definitionIdentifier: string;
    name: string;
    shortName: string;
    initials: string;
    isDefault: boolean;
}

export interface Category extends BaseCategoryDefinition {
    isDefault: boolean;
    definitions: DefinitionSummary[];
    subCategories: Category[];
}

/// /////////////////

export interface ConfigViewMetric extends BaseViewMetric<ConfigViewMetric> {}

export interface ConfigViewPillar
    extends BaseViewPillar<ConfigViewPillar, ConfigViewMetric> {
    identifier: string; // TODO revise. frontend only
}

export interface ConfigView
    extends BaseView<ConfigViewPillar, ConfigViewMetric> {}

export interface ConfigPathLevel extends BasePathLevel<ConfigPathLevel> {}

export interface ConfigPath extends BasePath<ConfigPathLevel> {}

export interface QuickSwitchItem<T = any> {
    name: string;
    shortName: string;
    initials: string;
    displayOrder: number;
    initialCriteria: string;
    icon: string;
    view: string;
    path: string;
    dateRanges: DateRange[];
    extra: T;
}

export enum QuickSwitchType {
    MARKET = 'market',
    LOB = 'lob',
    REGION = 'region',
    AREA = 'area',
    SITE = 'site',
    PEER_GROUP = 'peergroup',
    SUPER_GROUP = 'supergroup',
}

export const QuickSwitchTypeCriteriaIdTypeMap = {
    [QuickSwitchType.LOB]: CriteriaIdType.LOB_ID,
    [QuickSwitchType.REGION]: CriteriaIdType.REGION_ID,
    [QuickSwitchType.AREA]: CriteriaIdType.AREA_ID,
    [QuickSwitchType.SITE]: CriteriaIdType.SITE_ID,
    [QuickSwitchType.PEER_GROUP]: CriteriaIdType.PEER_GROUP_ID,
    [QuickSwitchType.SUPER_GROUP]: CriteriaIdType.SUPER_GROUP_ID,
};

export const CriteriaIdTypeViewContextKeyPartial = {
    [CriteriaIdType.LOB_ID]: 'Lob',
    [CriteriaIdType.REGION_ID]: 'Region',
    [CriteriaIdType.AREA_ID]: 'Area',
    [CriteriaIdType.SITE_ID]: 'Site',
    [CriteriaIdType.PEER_GROUP_ID]: 'PeerGroup',
    [CriteriaIdType.SUPER_GROUP_ID]: 'SuperGroup',
    [CriteriaIdType.QUEUE_ID]: 'Queue',
};

export interface QuickSwitch<T = any> {
    quickSwitchType: QuickSwitchType;
    title: string;
    buttonName: string;
    items: Array<QuickSwitchItem<T>>;
}

export function quickSwitchExclude<T extends QuickSwitchItem>(
    data: T[],
    ids: number[],
): T[] {
    return !ids || ids.length === 0
        ? // eslint-disable-next-line @typescript-eslint/prefer-includes
          data
        : // eslint-disable-next-line @typescript-eslint/prefer-includes
          data.filter((e) => ids.indexOf(parseInt(e.initialCriteria)) === -1);
}

export function quickSwitchInclude<T extends QuickSwitchItem>(
    data: T[],
    ids: number[],
): T[] {
    return !ids || ids.length === 0
        ? // eslint-disable-next-line @typescript-eslint/prefer-includes
          data
        : // eslint-disable-next-line @typescript-eslint/prefer-includes
          data.filter((e) => ids.indexOf(parseInt(e.initialCriteria)) !== -1);
}

export function quickSwitchThirdPartySites<T extends QuickSwitchItem>(
    data: T[],
    thirdPartySites: boolean,
): T[] {
    return thirdPartySites == null
        ? data
        : data.filter(
              (value) =>
                  value.extra.isThirdParty ===
                  this._filterContext.siteIsThirdParty,
          );
}

export function quickSwitchFilter(
    quickSwitch: QuickSwitch,
    viewContext: FilterContext,
): QuickSwitchItem[] {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (quickSwitch && quickSwitch.items) {
        const ct =
            QuickSwitchTypeCriteriaIdTypeMap[quickSwitch.quickSwitchType];
        let items = quickSwitch.items;
        if (viewContext) {
            const kp = CriteriaIdTypeViewContextKeyPartial[ct];
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            const excludedIds = viewContext[`excluded${kp}Ids`];
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            const includedIds = viewContext[`included${kp}Ids`];
            items = quickSwitchExclude(items, excludedIds);
            items = quickSwitchInclude(items, includedIds);
            if (ct === CriteriaIdType.SITE_ID) {
                items = quickSwitchThirdPartySites(
                    items,
                    viewContext.siteIsThirdParty,
                );
            }
        }
        return items;
    }
    return null;
}

export interface Metric {
    metricId: number;
    name: string;
    shortName: string;
    initials: string;
    rankOrder: string;
    weight: number;
}

export interface ConfigShallow extends BaseDefinitionShallow {}

export interface Config
    extends BaseDefinition<
        ConfigView,
        ConfigPath,
        ConfigViewPillar,
        ConfigViewMetric,
        ConfigPathLevel
    > {
    quickSwitches: QuickSwitch[];
    metrics: Metric[];
    disallowSetDefault: boolean;
    disallowSetFavourite: boolean;
}

/// /////////////////

export function checkPathLevels(
    levels: Array<PathLevel | ConfigPathLevel>,
): void {
    if (!levels) return;
    levels.sort((a, b) => a.displayOrder - b.displayOrder);
    levels.forEach((e) => {
        e.identifier = `${e.identifier}${Math.random()}`;
        checkPathLevels(e.levels);
    });
}

function checkViewMetrics(metrics: Array<ViewMetric | ConfigViewMetric>): void {
    if (!metrics) return;
    metrics.sort((a, b) => a.displayOrder - b.displayOrder);
    metrics.forEach((e) => checkViewMetrics(e.components));
}

function checkViewLayouts(
    view: View | ViewPillar | ConfigView | ConfigViewPillar,
): void {
    if (!view) return;
    view.layouts = view.layouts || [
        { breakpoint: 1600, width: 12, itemsPerRow: 1 },
    ];
}

function checkViewPillars(pillars: Array<ViewPillar | ConfigViewPillar>): void {
    if (!pillars) return;
    pillars.sort((a, b) => a.displayOrder - b.displayOrder);
    pillars.forEach((e) => {
        checkViewPillars(e.pillars);
        checkViewMetrics(e.metrics);
        checkViewLayouts(e);
        // TODO revise. frontend only
        (e as ConfigViewPillar).identifier = Math.random().toString();
    });
}

export class DefinitionTransformer
    implements ModelTransformer<Definition, Definition>
{
    transform(model: Definition): Definition {
        model.views = model.views || [];
        model.paths = model.paths || [];
        model.views.forEach((e) => {
            checkViewPillars(e.pillars);
            checkViewLayouts(e);
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
            e.attributes &&
                e.attributes.forEach(
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    (ee) => (ee.name = ee.name && ee.name.toLowerCase()),
                );
        });
        model.paths.forEach((e) => {
            checkPathLevels(e.levels);
        });
        model.paths.sort((a, b) => a.displayOrder - b.displayOrder);
        return model;
    }
}

export class DefinitionSummaryTransformer
    implements ModelTransformer<Category[], Category[]>
{
    transform(model: Category[]): Category[] {
        if (!model) return model;
        // eslint-disable-next-line no-return-assign
        model.forEach((e) => (e.definitions = e.definitions || []));
        // eslint-disable-next-line no-return-assign
        model.forEach((e) => (e.subCategories = e.subCategories || []));
        // eslint-disable-next-line no-return-assign
        model.forEach((e) =>
            e.subCategories.forEach(
                (ee) => (ee.definitions = ee.definitions || []),
            ),
        );
        return model;
    }
}

export class ConfigTransformer implements ModelTransformer<Config, Config> {
    transform(model: Config): Config {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        model.dashboardDateRanges &&
            model.dashboardDateRanges.forEach((e) => {
                e.start = e.start && new Date(e.start as any as string);
                e.end = e.end && new Date(e.end as any as string);
            });
        model.views = model.views || [];
        model.paths = model.paths || [];
        model.views.forEach((e) => {
            // e.viewIdentifier = `${e.viewIdentifier}${Math.random()}`;
            checkViewPillars(e.pillars);
            checkViewLayouts(e);
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
            e.attributes &&
                e.attributes.forEach(
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    (ee) => (ee.name = ee.name && ee.name.toLowerCase()),
                );
        });
        model.paths.forEach((e) => {
            // e.pathIdentifier = `${e.pathIdentifier}${Math.random()}`;
            checkPathLevels(e.levels);
        });
        model.paths.sort((a, b) => a.displayOrder - b.displayOrder);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        model.quickSwitches &&
            model.quickSwitches.forEach((e) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                e.items &&
                    e.items.forEach((ee) => {
                        if (!ee.dateRanges) return;
                        ee.dateRanges.forEach((eee) => {
                            eee.start = new Date(eee.start as any as string);
                            eee.end = new Date(eee.end as any as string);
                        });
                        ee.dateRanges.sort(
                            (a, b) => b.end.getTime() - a.end.getTime(),
                        );
                    });
            });
        return model;
    }
}

/// /////////////////

interface QuickSwitchOverride {
    initialCriteria: string;
    viewIdentifier: string;
    pathIdentifier: string;
}

export interface ConfigOverride {
    definitionIdentifier: string;
    initialCriteria: string;
    viewIdentifier: string;
    pathIdentifier: string;
    period: PeriodType;
    quickSwitch: QuickSwitchOverride;
    displayOrder: number;
}

export interface Override<T = any> {
    configOverrides: ConfigOverride[];
    extra: T;
}

/// /////////////////

// see DataAuthorizationModel
export enum SystemComponent {
    PERFORMANCE_INSIGHT = 17,
    HUMAN_INSIGHT = 26,
}

// TODO revise. should probably not be here, but using scope/securityGroup/permission
export interface DataAuthorizationModel {
    moduleId: number;
    componentId: number;
    entityId: number;
    granted: number;
    isDefault: boolean;
}

/// /////////////////

export interface RoleHierarchyItem {
    hierarchyTypeId: number;
    roleId: number;
    parentRoleId: number;
    startDate: Date;
    endDate: Date;
    createdByUserId: number;
    createdOn: Date;
    lastUpdatedByUserId: number;
    lastUpdatedOn: Date;
}

// TODO move to user.service? merge from tdcf HierarchyInfoItem?
export interface HierarchyUserInfoItem {
    roleId: number;
    lobId: number;
    regionId: number;
    areaId: number;
    siteId: number;
    peerGroupId: number;
    superGroupId: number;
    managerId: number;
    userId: number;
    startDate: Date;
    endDate: Date;
}

/// /////////////////

export interface DataKey {
    levelId: string;
    levelIdPath: string;
    depth?: number;
    data: MetricData[];
    autoExpand?: number[]; // MetricData.id | flag it here just so that ViewContext/FilterContext doesn't have to reach the grid
    queueLookupSuperGroup?: Record<string, boolean>;
}

export function findLevel(
    levelTree: ConfigPathLevel[],
    identifier: string,
    path?: string[],
): ConfigPathLevel {
    if (!identifier) return null;
    // eslint-disable-next-line @typescript-eslint/no-for-in-array
    for (const i in levelTree) {
        const l = levelTree[i];
        if (l.identifier === identifier) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            path && path.push(l.identifier);
            return l;
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-for-in-array
    for (const i in levelTree) {
        let l = levelTree[i];
        if (l.levels && l.levels.length !== 0) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            path && path.push(l.identifier);
            l = findLevel(l.levels, identifier, path);
            if (l) {
                return l;
            } else {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                path && path.pop();
            }
        }
    }
    return null;
}

export function createLevelPath(
    levelTree: ConfigPathLevel[],
    identifier: string,
): string {
    const path = ['ROOT'];
    if (identifier === path[0]) return path[0];
    const l = findLevel(levelTree, identifier, path);
    return !l ? null : path.join('|');
}

export function flatten<TIn, TOut>(
    arr: TIn[],
    propConcat: (i: TIn) => TOut | TOut[],
    propArr: (i: TIn) => TIn[],
    flat: TOut[] = [],
): TOut[] {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    arr &&
        arr.forEach((e) => {
            const result = propConcat(e);
            if (Array.isArray(result)) {
                flat = flat.concat(result);
            } else {
                flat.push(result);
            }
            flat = flatten(propArr(e), propConcat, propArr, flat);
        });
    return flat;
}

/**
 * @deprecated
 */
export function flattenMetrics(
    pillars: ConfigViewPillar[],
    metrics: number[] = [],
): number[] {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    pillars &&
        pillars.forEach((e) => {
            metrics = metrics.concat(e.metrics.map((ee) => ee.metricId));
            metrics = flattenMetrics(e.pillars, metrics);
        });
    return metrics;
}

export function padMissingMetricsWithMetrics(
    metrics: MetricDataItem[],
    expectedMetrics: Metric[],
): MetricDataItem[] {
    const ret = metrics || [];
    // eslint-disable-next-line array-callback-return
    expectedMetrics.map((value2) => {
        const metric = ret.find((value3) => value3.id === value2.metricId);
        if (!metric) {
            ret.push(
                createMetricDataItem({
                    id: value2.metricId,
                    name: value2.name,
                    shortName: value2.shortName,
                    initials: value2.initials,
                    weight: value2.weight,
                }),
            );
        }
    });
    return ret;
}

/**
 * @deprecated
 */
export function flattenSupported(
    metrics: ConfigViewMetric[],
    supported: number[] = [],
): number[] {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    metrics &&
        metrics.forEach((e) => {
            supported = supported.concat(e.supportedColumns);
            supported = flattenSupported(e.components, supported);
        });
    return supported;
}

export function shouldGotoDashboard(
    dashboardDateRanges: DateRange[],
    eventDate: Date,
): boolean {
    return (
        dashboardDateRanges &&
        dashboardDateRanges.findIndex((e) => {
            if (e.start == null && e.end == null) return false;
            const matchStart = e.start == null || eventDate >= e.start;
            const matchEnd = e.end == null || e.end >= eventDate;
            return matchStart && matchEnd;
        }) !== -1
    );
}

export interface TrendPeriodStart {
    periodType: PeriodType;
    startDate: Date;
}

export function determineTrendPeriods(
    eventDate: Date,
    cacheDates: CacheDateItem[],
): TrendPeriodStart[] {
    const trendPeriods = [];
    if (eventDate) {
        let time = eventDate.getTime();

        const pre = new Date();
        pre.setDate(-90);
        if (time >= pre.getTime()) {
            trendPeriods.push({ periodType: PeriodType.DAY, startDate: pre });
        }

        [PeriodType.WEEK, PeriodType.MONTH].forEach((p) => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            const cds =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                cacheDates &&
                cacheDates
                    .filter((e) => e.periodType === p)
                    .sort(
                        (a, b) => b.eventDate.getTime() - a.eventDate.getTime(),
                    );
            // go the long way so that endDate/CacheDateItem doesn't have to go into the store
            const ecd = cds.find((e) => e.eventDate.getTime() === time);
            if (ecd) {
                time = ecd.endDate.getTime();
            }
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            const cd =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                cds &&
                cds.find(
                    (e) =>
                        e.startDate.getTime() <= time &&
                        time <= e.endDate.getTime(),
                );
            cd &&
                trendPeriods.push({
                    periodType: p,
                    startDate: cds[cds.length - 1].startDate,
                });

            if (p === PeriodType.WEEK && cds.length > 0 && !cd) {
                trendPeriods.push({
                    periodType: p,
                    startDate: cds[cds.length - 1].startDate, // Oldest available week
                });
            }
        });
    }
    return trendPeriods;
}

export function getUniqueQueueSuperGroupCombinations(
    superGroupQueues: SuperGroupQueue[],
): Record<string, boolean> {
    const combinations: Record<string, boolean> = {};

    superGroupQueues?.forEach((data) => {
        const key = `${data?.queueId}-${data?.superGroupId}-${data?.userId}`;
        combinations[key] = true;
    });

    return combinations;
}
